import { Component, OnInit, AfterViewInit, OnDestroy, Directive } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Router, ActivatedRoute, Params } from '@angular/router';
import {
  ModalService, ProxyService,  AlertService, ResolutionService, AuthTrackingService, BrazeService, JitMigrationService,
  CiamFlagsService
} from '@core/services';

import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs';


import { ApiRoutes } from '@shared/models/configs/api.endpoints';

import { environment } from '@environments/environment';
import { ResultEntity, HomeResponse } from '@shared/models/feature-page';
import { LoggingService } from '@core/services/logging.service';
import { GtmService } from '@core/services/gtm.service';
import { GtmExternal } from '@shared/models/configs/gtm.external';
import { Constants } from '@app/core/constants/core.constants';
declare global {
  interface Window {
    QSI: any;
  }
}
@Component({
  selector: 'app-home',
  animations: [
    trigger('fade', [
      state('shown', style({ opacity: 1, visibility: 'visible', height: '*' })),
      state('hidden', style({ opacity: 0, visibility: 'hidden', height: 0 })),
      transition('shown => hidden', animate('600ms ease-in')),
      transition('hidden => shown', animate('600ms ease-in')),
    ])
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.less'],
  providers: [AlertService]
})

export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {

  private subscriptions = new Subscription();

  protected homeList: Array<ResultEntity>;
  protected imageUrlSvr = environment.IMG_ENDPOINT + '/home/';
  protected isMobileWidth: boolean;

  private returnUrl: string;

  public loaded: boolean;
  public imagesLoaded: boolean;

  private homeNudgeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private activatedRoute: ActivatedRoute,
    private proxyService: ProxyService,
    private modalService: ModalService,
    public alertService: AlertService,
    private resolution: ResolutionService,
    private titleService: Title,
    private authTracking: AuthTrackingService,
    private router: Router,
    private logger: LoggingService,
    private gtmService: GtmService,
    private brazeService: BrazeService,
    private jitMigrationService: JitMigrationService,
    private ciamFlagsService: CiamFlagsService
  ) {
    this.isMobileWidth = false;
    this.titleService.setTitle('Whataburger | Order Online with Curbside and Delivery');
    this.loaded = false;
    this.imagesLoaded = false;
    this.homeList = new Array<ResultEntity>();
    this.returnUrl = '';
    // this.uuid = Guid.create();
  }

  ngOnInit() {
    sessionStorage.removeItem('childRecipe');
    sessionStorage.removeItem('linkedRecipeBack');
    // const deviceId = this.uuid.toString();
    // localStorage.getItem('deviceId') ? '' : localStorage.setItem('deviceId', deviceId);
    this.subscriptions.add(
      this.proxyService.httpGet(ApiRoutes.CMS_FEATURES).subscribe(
        (success) => {
          this.processResponse(success.body.valueOf());
        },
        (error) => {
          this.processErr(error);
        }
      )
    );

    this.isLoggedIn();

    this.scheduleMigrationNudge();

    this.subscriptions.add(
      this.resolution.width.subscribe(
        (width) => {
          if (width > environment.MAX_MOBILE_WIDTH) {
            this.isMobileWidth = false;
          } else {
            this.isMobileWidth = true;
          }
        }
      )
    );
  }

  isLoggedIn() {
    return this.authTracking.isLoggedIn();
  }

  /**
   * The mandatory delay is near-zero on purpose. Cancelling on navigate-away is right for a nudge the
   * user may decline, but on a mandatory one a long delay is just an escape hatch.
   */
  private scheduleMigrationNudge() {
    if (!this.authTracking.isLoggedIn()) {
      return;
    }

    const config = this.ciamFlagsService.getNudgeConfig();
    const decision = this.jitMigrationService.peekDecision('home');
    const delaySeconds = decision.isMandatory ? config.mandatoryDisplayDelaySeconds : config.homeDisplayDelaySeconds;

    this.homeNudgeTimer = setTimeout(
      () => {
        this.homeNudgeTimer = null;
        this.jitMigrationService.checkAndShowModal('home');
      },
      delaySeconds * 1000
    );
  }

  processResponse(body: any) {
    this.homeList = <Array<ResultEntity>>(<HomeResponse>body).result;
    for (const result of this.homeList) {
      // the point of this code block is to make sure that buttonLoc and the offsets have values
      // as they are required in the HTML even if the web admin doesn't pass those values.
      // Default value is LEFT for the location and 0 for the offsets

      // defaulting to 0 as there should only ever be 1 active and it is always the first
      const extraContent = result.homePageAdditionalContents[0];
      if (extraContent.buttonLoc === undefined || extraContent.buttonLoc === null) {
        extraContent.buttonLoc = 'left';
      }

      if (extraContent.offsetX === undefined || extraContent.offsetX === null) {
        extraContent.offsetX = 0;
      }

      if (extraContent.offsetY === undefined || extraContent.offsetY === null) {
        extraContent.offsetY = 0;
      }

      result.show = false;
    }

    this.loaded = true;
  }

  showTiles(feature: ResultEntity) {
    feature.show = true;
  }

  finishedLoading() {
    this.imagesLoaded = true;
  }

  processErr(error: any) {
    this.alertService.error(error.message);
    this.logger.logError(error, 'Error retrivinge home features ', JSON.stringify(error));
  }

  ngAfterViewInit() {
    this.gtmService.sendPageName(GtmExternal.VALUE_HOMEPAGE);

    // This displays the sign in modal if there is a query param set to it
    // This should only occur if the user tried to go to a page that requires
    // them to be logged into their account and they hadn't logged in first.
    // The authentication guard service will redirect the user to the home page
    // and attach query parameters to the browser URL letting the home page
    // know to pop up the sign in component as well as redirect the user
    // back to the previous requested page once sign in is completed.
    this.subscriptions.add(this.activatedRoute.queryParams.subscribe((params: Params) => {
      // TODO - Need to verify later after finalizing on deep linking by Tim and Sandip
      const signIn = params[Constants.SIGNIN];
      this.returnUrl = params[Constants.RETURN_URL];
      const reward = params[Constants.REWARDS];
      if (signIn && this.isLoggedIn() === false) {

          if (!params[Constants.APPLE_SIGNIN_ORIGIN]) {
            this.openSignInModalPopUp();
          }
      } else if (reward) {
        this.authenticateToRewards();
      } else if (this.returnUrl && this.isLoggedIn()) {
        this.router.navigateByUrl(decodeURIComponent(this.returnUrl));
      } else {
        this.router.navigate(['home'], {queryParamsHandling: 'preserve' });
      }
      // else if (orderId) {
      //   this.reorderViaemail(orderId);
      // }
    }));
      this.subscriptions.add(this.authTracking.user.subscribe(
        user => {
          if (user) {
            this.brazeService.updateUser(user);

            if(typeof window.QSI === 'undefined') {
              window.QSI = {};
            }
            window.QSI.config = {     
              externalReference: user?.id      
            };
          }
        }
      ));
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    if (this.homeNudgeTimer) {
      //without this, leaving Home inside the delay window still fires the callback and the nudge lands
      //on whatever route the user moved to
      clearTimeout(this.homeNudgeTimer);
      this.homeNudgeTimer = null;
      //only worth logging for someone who would actually have seen it, otherwise every logged-in user
      //who leaves Home quickly counts as a suppression
      if (this.jitMigrationService.peekDecision('home').canShow) {
        this.jitMigrationService.recordNudgeCancelled('home');
      }
    }
  }

  // authenticateToRewards
  authenticateToRewards() {
    if (this.isLoggedIn()) {
      this.router.navigate(['/menu']);
    } else {
      this.openSignInModalPopUp();
    }
  }
  openSignInModalPopUp() {
    setTimeout(() => {
      this.modalService.setModalSelector(this.modalService.getSignInModalSelector());
      this.modalService.showModal();
    }, 1000);
  }
}
